import { supabase } from './supabase';
import { getExecutionStepsForDisplay, stepsToDescription } from '../utils/exerciseDisplayUtils';
import { Exercise, CreateExerciseData, TrainerExerciseListRow } from '../types/dataModel';

function rowToExercise(row: Record<string, any>): Exercise {
  const rawSteps = Array.isArray(row.steps) ? row.steps : [];
  return {
    id: row.id,
    name: row.name,
    muscleGroup: row.muscle_group ?? undefined,
    equipment: row.equipment ?? undefined,
    description: row.description,
    steps: getExecutionStepsForDisplay(row.description, rawSteps),
    image: row.image,
    video: row.video,
    isPublic: row.is_public,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

function applyOverride(exercise: Exercise, override: Record<string, any> | null): Exercise {
  if (!override) return exercise;
  const mergedDesc =
    override.description !== undefined && override.description !== null
      ? override.description
      : exercise.description;
  const rawSteps =
    Array.isArray(override.steps) && override.steps.length > 0 ? override.steps : exercise.steps;
  return {
    ...exercise,
    name: override.name ?? exercise.name,
    muscleGroup: override.muscle_group ?? exercise.muscleGroup,
    equipment: override.equipment !== undefined && override.equipment !== null ? override.equipment : exercise.equipment,
    description: mergedDesc,
    steps: getExecutionStepsForDisplay(mergedDesc, rawSteps),
    image: override.image ?? exercise.image,
    video: override.video ?? exercise.video,
    isPublic: override.is_public !== undefined && override.is_public !== null ? override.is_public : exercise.isPublic
  };
}

export interface ExerciseFilters {
  category?: string;
  muscleGroup?: string;
  equipment?: string;
  difficulty?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ExercisePageFilters {
  search?: string;
  muscleGroup?: string;
  equipment?: string;
  limit?: number;
  offset?: number;
  trainerId?: string;
  sortColumn?: 'name' | 'muscleGroup' | 'equipment' | 'createdBy' | 'createdAt';
  sortDirection?: 'asc' | 'desc';
  createdByOrder?: 'system-first' | 'trainer-first';
  includeCount?: boolean;
  includeOverrides?: boolean;
}

export interface ExercisePageResult {
  exercises: Exercise[];
  totalCount?: number;
}

export interface ExerciseCountFilters {
  search?: string;
  muscleGroup?: string;
  equipment?: string;
  trainerId?: string;
}

const EXERCISES_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let exercisesCache: { key: string; data: Exercise[]; timestamp: number } | null = null;
const exercisePageInFlight = new Map<string, Promise<ExercisePageResult>>();
const exerciseCountInFlight = new Map<string, Promise<number>>();
const EXERCISE_OVERRIDE_FULL_SELECT_COLUMNS = '*';
const EXERCISE_OVERRIDE_TABLE_SELECT_COLUMNS = 'exercise_id,name,muscle_group,equipment';
let exerciseTimingRequestSeq = 0;

// Module-level warmup promise. Populated by warmupExerciseDB() so that
// getExercisesPage can yield to it instead of racing the cold-start.
let _dbWarmupPromise: Promise<void> | null = null;

/**
 * Prefetch the default first page of exercises to wake a paused Supabase
 * instance AND load the exercise heap pages into buffer cache.
 *
 * Using the page query (not just a count) means the DB caches the actual
 * name/muscle_group/equipment/created_by heap pages, so the real page query
 * that fires after this promise resolves is a near-instant cache hit.
 *
 * Call as early as possible (e.g. when auth user is available).
 * Safe to call multiple times — only the first call has any effect.
 */
export function warmupExerciseDB(trainerId: string): void {
  if (_dbWarmupPromise) return;
  _dbWarmupPromise = Promise.resolve(
    supabase.rpc('get_exercise_library_page', {
      p_trainer_id:  trainerId,
      p_search:      null,
      p_sort_col:    'name',
      p_sort_dir:    'asc',
      p_nulls_first: false,
      p_limit:       30,
      p_offset:      0,
    })
  ).then(() => {}).catch(() => {});
}

function getTimingNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function formatTimingMs(startMs: number): string {
  return `${(getTimingNow() - startMs).toFixed(1)}ms`;
}

function logExerciseTiming(requestId: number, stage: string, details?: Record<string, unknown>): void {
  if (details) {
    console.log(`[ExerciseTableTiming][${requestId}] ${stage}`, details);
    return;
  }
  console.log(`[ExerciseTableTiming][${requestId}] ${stage}`);
}

function getExercisesCacheKey(filters: ExerciseFilters): string {
  return JSON.stringify(filters);
}

function clearExercisesCache(): void {
  exercisesCache = null;
}

async function applyOverridesToExercises(
  exercises: Exercise[],
  trainerId?: string | null,
  mode: 'full' | 'table' = 'full',
  timingRequestId?: number
): Promise<Exercise[]> {
  if (exercises.length === 0) return exercises;

  let effectiveTrainerId = trainerId ?? null;
  if (!effectiveTrainerId) {
    const authStart = getTimingNow();
    const { data: { user } } = await supabase.auth.getUser();
    if (timingRequestId !== undefined) {
      logExerciseTiming(timingRequestId, 'auth.getUser() for override lookup completed', {
        duration: formatTimingMs(authStart),
      });
    }
    effectiveTrainerId = user?.id ?? null;
  }
  if (!effectiveTrainerId) return exercises;

  const ids = exercises.map((exercise) => exercise.id);
  const BATCH = 50;
  const allOverrides: Record<string, any>[] = [];

  for (let i = 0; i < ids.length; i += BATCH) {
    const chunk = ids.slice(i, i + BATCH);
    const batchStart = getTimingNow();
    const { data: overrides } = await supabase
      .from('exercise_override')
      .select(mode === 'table' ? EXERCISE_OVERRIDE_TABLE_SELECT_COLUMNS : EXERCISE_OVERRIDE_FULL_SELECT_COLUMNS)
      .eq('trainer_id', effectiveTrainerId)
      .in('exercise_id', chunk);

    if (timingRequestId !== undefined) {
      logExerciseTiming(timingRequestId, 'exercise_override query completed', {
        duration: formatTimingMs(batchStart),
        batchSize: chunk.length,
        mode,
        returnedOverrides: overrides?.length ?? 0,
      });
    }

    if (overrides?.length) {
      allOverrides.push(...overrides);
    }
  }

  if (allOverrides.length === 0) return exercises;

  const byExerciseId = allOverrides.reduce<Record<string, any>>((acc, override) => {
    acc[override.exercise_id] = override;
    return acc;
  }, {});

  return exercises.map((exercise) => applyOverride(exercise, byExerciseId[exercise.id] ?? null));
}

export class ExerciseService {
  // Get all exercises for the current user (public + their own)
  static async getExercises(filters: ExerciseFilters = {}): Promise<Exercise[]> {
    try {
      const key = getExercisesCacheKey(filters);
      if (exercisesCache && exercisesCache.key === key && Date.now() - exercisesCache.timestamp < EXERCISES_CACHE_TTL_MS) {
        return exercisesCache.data;
      }

      let query = supabase
        .from('exercise')
        .select('*')
        .order('name');

      // Apply filters
      if (filters.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }

      if (filters.difficulty && filters.difficulty !== 'all') {
        query = query.eq('difficulty', filters.difficulty);
      }

      if (filters.muscleGroup && filters.muscleGroup !== 'all') {
        query = query.contains('muscle_groups', [filters.muscleGroup]);
      }

      if (filters.equipment && filters.equipment !== 'all') {
        query = query.contains('equipment', [filters.equipment]);
      }

      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      // Pagination
      if (filters.page && filters.limit) {
        const from = (filters.page - 1) * filters.limit;
        const to = from + filters.limit - 1;
        query = query.range(from, to);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching exercises:', error);
        throw error;
      }

      const list = await applyOverridesToExercises((data?.map(row => rowToExercise(row)) ?? []) as Exercise[]);
      exercisesCache = { key, data: list, timestamp: Date.now() };
      return list;
    } catch (error) {
      console.error('Error in getExercises:', error);
      throw error;
    }
  }

  /**
   * Fetch multiple exercises by ID with optional trainer overrides in 2 queries (batch).
   * Use this instead of calling getExerciseById in a loop to avoid N+1 and repeated getUser().
   */
  static async getExercisesByIds(ids: string[], trainerId?: string | null): Promise<Record<string, Exercise>> {
    if (ids.length === 0) return {};
    try {
      let effectiveTrainerId = trainerId ?? null;
      if (!effectiveTrainerId) {
        const { data: { user } } = await supabase.auth.getUser();
        effectiveTrainerId = user?.id ?? null;
      }

      const { data: rows, error } = await supabase
        .from('exercise')
        .select('*')
        .in('id', ids);

      if (error) {
        console.error('Error fetching exercises by ids:', error);
        throw error;
      }
      const exercises = (rows ?? []).map(rowToExercise);
      const byId: Record<string, Exercise> = {};
      for (const ex of exercises) byId[ex.id] = ex;

      if (effectiveTrainerId && exercises.length > 0) {
        const BATCH = 50;
        const allOverrides: Record<string, any>[] = [];
        for (let i = 0; i < ids.length; i += BATCH) {
          const chunk = ids.slice(i, i + BATCH);
          const { data: overrides } = await supabase
            .from('exercise_override')
            .select('*')
            .eq('trainer_id', effectiveTrainerId)
            .in('exercise_id', chunk);
          if (overrides?.length) allOverrides.push(...overrides);
        }
        const overrideByExerciseId = allOverrides.reduce<Record<string, any>>((acc, o) => {
          acc[o.exercise_id] = o;
          return acc;
        }, {});
        for (const id of Object.keys(byId)) {
          byId[id] = applyOverride(byId[id], overrideByExerciseId[id] ?? null);
        }
      }
      return byId;
    } catch (error) {
      console.error('Error in getExercisesByIds:', error);
      throw error;
    }
  }

  static async getExercisesPage(filters: ExercisePageFilters = {}): Promise<ExercisePageResult> {
    const requestKey = JSON.stringify(filters);
    const inFlight = exercisePageInFlight.get(requestKey);
    if (inFlight) {
      const requestId = ++exerciseTimingRequestSeq;
      logExerciseTiming(requestId, 'reused in-flight getExercisesPage request', {
        requestKey,
      });
      return inFlight;
    }

    const requestPromise = (async () => {
      const requestId = ++exerciseTimingRequestSeq;
      const overallStart = getTimingNow();
      logExerciseTiming(requestId, 'getExercisesPage started', {
        filters,
      });

      let trainerId = filters.trainerId ?? null;
      if (!trainerId) {
        const authStart = getTimingNow();
        const { data: { user } } = await supabase.auth.getUser();
        logExerciseTiming(requestId, 'auth.getUser() completed', {
          duration: formatTimingMs(authStart),
        });
        trainerId = user?.id ?? null;
      }

      // Map frontend sort column names to the values accepted by the RPC function.
      // The RPC uses UNION ALL + SECURITY DEFINER to avoid the BitmapOr plan and
      // per-row RLS overhead that made the OR-predicate PostgREST query slow on
      // cold cache.
      const sortColMap: Record<string, string> = {
        name: 'name',
        muscleGroup: 'muscleGroup',
        equipment: 'equipment',
        createdBy: 'createdBy',
      };
      const rpcSortCol = sortColMap[filters.sortColumn ?? 'name'] ?? 'name';
      const rpcSortDir = filters.sortDirection ?? 'asc';
      // For createdBy sort: NULL (system rows) placement is controlled by p_nulls_first.
      // system-first = NULLs come first on ASC → nulls_first true when system-first.
      const nullsFirst = filters.sortColumn === 'createdBy'
        ? filters.createdByOrder !== 'trainer-first'
        : false;

      // If a DB warmup is in flight, wait for it (max 12s) before firing the
      // page query. This ensures the page query hits a warm instance rather
      // than racing the cold-start alongside the warmup call.
      if (_dbWarmupPromise) {
        await Promise.race([
          _dbWarmupPromise,
          new Promise<void>((resolve) => setTimeout(resolve, 12_000)),
        ]);
      }

      const exerciseQueryStart = getTimingNow();
      const { data, error } = await supabase.rpc('get_exercise_library_page', {
        p_trainer_id:  trainerId,
        p_search:      filters.search?.trim() || null,
        p_sort_col:    rpcSortCol,
        p_sort_dir:    rpcSortDir,
        p_nulls_first: nullsFirst,
        p_limit:       filters.limit ?? 30,
        p_offset:      filters.offset ?? 0,
      });
      logExerciseTiming(requestId, 'exercise rpc query completed', {
        duration: formatTimingMs(exerciseQueryStart),
        rowCount: (data as any[])?.length ?? 0,
      });

      if (error) {
        console.error('Error fetching paginated exercises via RPC:', error);
        throw error;
      }

      let exercises = ((data ?? []) as Record<string, any>[]).map((row) => rowToExercise(row));
      if (filters.includeOverrides !== false) {
        const overrideStart = getTimingNow();
        exercises = await applyOverridesToExercises(
          exercises,
          trainerId,
          'table',
          requestId
        );
        logExerciseTiming(requestId, 'override application completed', {
          duration: formatTimingMs(overrideStart),
        });
      } else {
        logExerciseTiming(requestId, 'override application skipped');
      }

      logExerciseTiming(requestId, 'getExercisesPage finished', {
        duration: formatTimingMs(overallStart),
      });

      return {
        exercises,
        totalCount: undefined,
      };
    })();

    exercisePageInFlight.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } catch (error) {
      console.error('Error in getExercisesPage:', error);
      throw error;
    } finally {
      if (exercisePageInFlight.get(requestKey) === requestPromise) {
        exercisePageInFlight.delete(requestKey);
      }
    }
  }

  static async getExercisesCount(filters: ExerciseCountFilters = {}): Promise<number> {
    const requestKey = JSON.stringify(filters);
    const inFlight = exerciseCountInFlight.get(requestKey);
    if (inFlight) return inFlight;

    const requestPromise = (async () => {
      const requestId = ++exerciseTimingRequestSeq;
      const overallStart = getTimingNow();
      logExerciseTiming(requestId, 'getExercisesCount started', { filters });

      let trainerId = filters.trainerId ?? null;
      if (!trainerId) {
        const authStart = getTimingNow();
        const { data: { user } } = await supabase.auth.getUser();
        logExerciseTiming(requestId, 'auth.getUser() completed for count', {
          duration: formatTimingMs(authStart),
        });
        trainerId = user?.id ?? null;
      }

      const countStart = getTimingNow();
      const { data: countData, error } = await supabase.rpc('get_exercise_library_count', {
        p_trainer_id: trainerId,
        p_search:     filters.search?.trim() || null,
      });
      logExerciseTiming(requestId, 'exercise count rpc completed', {
        duration: formatTimingMs(countStart),
        totalCount: countData ?? 0,
      });

      if (error) {
        console.error('Error fetching exercise count via RPC:', error);
        throw error;
      }

      logExerciseTiming(requestId, 'getExercisesCount finished', {
        duration: formatTimingMs(overallStart),
      });

      return (countData as number) ?? 0;
    })();

    exerciseCountInFlight.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } catch (error) {
      console.error('Error in getExercisesCount:', error);
      throw error;
    } finally {
      if (exerciseCountInFlight.get(requestKey) === requestPromise) {
        exerciseCountInFlight.delete(requestKey);
      }
    }
  }

  static async applyExerciseTableOverrides(
    exercises: Exercise[],
    trainerId?: string | null
  ): Promise<Exercise[]> {
    return applyOverridesToExercises(exercises, trainerId, 'table');
  }

  /**
   * Lightweight HEAD count of exercises visible to the trainer for the Library badge.
   *
   * MUST stay in sync with the visibility predicate of {@link getTrainerExerciseList}.
   * Today: `is_public = true` OR `created_by = trainerId`. If that list query gains
   * a soft-delete filter (`deleted_at IS NULL`), system filter, etc., mirror it here
   * or the badge will lie. No rows are transferred — headers-only count.
   */
  static async getExercisesCountForTrainer(trainerId: string): Promise<number> {
    if (!trainerId) return 0;
    try {
      const { count, error } = await supabase
        .from('exercise')
        .select('id', { count: 'exact', head: true })
        .or(`is_public.eq.true,created_by.eq.${trainerId}`);
      if (error) throw error;
      return count ?? 0;
    } catch (error) {
      console.error('Error in getExercisesCountForTrainer:', error);
      throw error;
    }
  }

  /**
   * Trainer Library Exercises tab — single client-side load (handoff §3.1).
   * Returns every exercise visible to the trainer (`is_public = true` OR `created_by = trainerId`)
   * with name/muscle/equipment optionally overridden by the trainer's `exercise_override` row.
   * Hard cap at 2000 rows; warns on console if hit. One PostgREST round-trip + one override fetch.
   */
  static async getTrainerExerciseList(trainerId: string): Promise<TrainerExerciseListRow[]> {
    if (!trainerId) return [];
    const HARD_CAP = 2000;

    const { data, error } = await supabase
      .from('exercise')
      .select('id, name, muscle_group, equipment, created_by, is_public')
      .or(`is_public.eq.true,created_by.eq.${trainerId}`)
      .order('name', { ascending: true })
      .limit(HARD_CAP);

    if (error) {
      console.error('getTrainerExerciseList:', error);
      throw error;
    }

    const rows: TrainerExerciseListRow[] = (data ?? []).map((r: Record<string, any>) => ({
      id: r.id,
      name: r.name,
      muscleGroup: r.muscle_group ?? null,
      equipment: r.equipment ?? null,
      createdBy: r.created_by ?? null,
      isPublic: !!r.is_public,
    }));

    /* Trainer overrides — rename / re-tag of system rows. Best-effort; failure leaves canonical values. */
    try {
      const ids = rows.map((r) => r.id);
      if (ids.length > 0) {
        const { data: overrideRows, error: overrideError } = await supabase
          .from('exercise_override')
          .select(EXERCISE_OVERRIDE_TABLE_SELECT_COLUMNS)
          .eq('trainer_id', trainerId)
          .in('exercise_id', ids);
        if (overrideError) {
          console.warn('getTrainerExerciseList: override fetch failed', overrideError);
        } else if (overrideRows && overrideRows.length > 0) {
          const byId = new Map<string, Record<string, any>>(
            overrideRows.map((o: Record<string, any>) => [o.exercise_id, o] as const)
          );
          for (let i = 0; i < rows.length; i++) {
            const ov = byId.get(rows[i].id);
            if (!ov) continue;
            rows[i] = {
              ...rows[i],
              name: ov.name ?? rows[i].name,
              muscleGroup: ov.muscle_group ?? rows[i].muscleGroup,
              equipment: ov.equipment ?? rows[i].equipment,
            };
          }
          /* Re-sort by (overridden) name so a rename does not desync alphabetical order. */
          rows.sort((a, b) => a.name.localeCompare(b.name));
        }
      }
    } catch (overrideErr) {
      console.warn('getTrainerExerciseList: override apply failed', overrideErr);
    }

    if (rows.length >= HARD_CAP) {
      console.warn(`[ExerciseList] hit cap of ${HARD_CAP}`);
    }
    return rows;
  }

  /**
   * Trainer Library bulk duplicate (handoff §4.3, Item 3).
   *
   * Trainers can duplicate ANY exercise on the Library tab, including system Bravos rows.
   * The duplicate is always a trainer-owned private record (`created_by = trainerId`,
   * `is_public = false`) with `name = "<original> (copy)"`. If the trainer already owns
   * an exercise with that name (or a `(copy N)` variant), we append a numeric suffix
   * `(copy 2)`, `(copy 3)`, … using the same pattern as `generateUniqueWorkoutName`
   * (TrainerDashboard.tsx). The caller passes the trainer's currently visible exercises
   * so we can pre-compute unique names in one shot without N round-trips.
   *
   * Returns the newly-created exercise rows (without override application — caller refreshes
   * the list afterwards via the normal `loadAllExercisesForLibrary` path).
   */
  static async duplicateExercises(
    ids: string[],
    trainerId: string,
    existingTrainerNames: Set<string> = new Set()
  ): Promise<Exercise[]> {
    if (ids.length === 0 || !trainerId) return [];

    /* Fetch only the columns we'll write back. Keep the SELECT minimal so the
     * function tolerates schema drift (e.g. older `exercise` shapes without
     * `steps`); the trainer copy uses the same fields as `createExercise`. */
    const { data: sourceRows, error: fetchError } = await supabase
      .from('exercise')
      .select('id, name, muscle_group, equipment, description, image, video')
      .in('id', ids);

    if (fetchError) {
      console.error('[duplicateExercises] fetch source rows failed:', fetchError);
      const message = fetchError.message || 'Could not read the selected exercises.';
      throw new Error(`Duplicate failed (fetch): ${message}`);
    }
    if (!sourceRows || sourceRows.length === 0) {
      throw new Error('Duplicate failed: none of the selected exercises could be loaded.');
    }

    /* Working set so suffixes don't collide within this batch either. */
    const usedNames = new Set<string>(Array.from(existingTrainerNames));

    const buildUniqueCopyName = (base: string): string => {
      const candidate = `${base} (copy)`;
      if (!usedNames.has(candidate)) {
        usedNames.add(candidate);
        return candidate;
      }
      let n = 2;
      while (usedNames.has(`${base} (copy ${n})`)) n++;
      const name = `${base} (copy ${n})`;
      usedNames.add(name);
      return name;
    };

    /* Mirror the shape of `createExercise`'s base payload — known to satisfy
     * the table's INSERT RLS + NOT NULL constraints. Omit `steps` here for the
     * same schema-drift reason explained above; overrides can re-add it later. */
    const payloads = sourceRows.map((row: Record<string, any>) => ({
      name: buildUniqueCopyName(row.name),
      muscle_group: row.muscle_group ?? null,
      equipment: row.equipment ?? null,
      description: row.description ?? null,
      image: row.image ?? null,
      video: row.video ?? null,
      is_public: false,
      created_by: trainerId,
    }));

    const { data: inserted, error: insertError } = await supabase
      .from('exercise')
      .insert(payloads)
      .select('*');

    if (insertError) {
      console.error('[duplicateExercises] insert failed:', insertError, { payloadCount: payloads.length });
      const code = (insertError as { code?: string }).code;
      const details = (insertError as { details?: string }).details;
      const baseMessage = insertError.message || 'Insert failed.';
      if (code === '23505') {
        throw new Error('Duplicate failed: an exercise with that name already exists in your library.');
      }
      if (code === '23503') {
        throw new Error('Duplicate failed: your account is not yet registered as a trainer in the database. Contact support.');
      }
      throw new Error(`Duplicate failed${code ? ` [${code}]` : ''}: ${baseMessage}${details ? ` — ${details}` : ''}`);
    }

    clearExercisesCache();
    return ((inserted ?? []) as Record<string, any>[]).map(rowToExercise);
  }

  /** Single-row convenience wrapper around {@link duplicateExercises}. */
  static async duplicateExercise(
    id: string,
    trainerId: string,
    existingTrainerNames: Set<string> = new Set()
  ): Promise<Exercise | null> {
    const [first] = await ExerciseService.duplicateExercises([id], trainerId, existingTrainerNames);
    return first ?? null;
  }

  /**
   * Trainer Library bulk delete (handoff §5.4). Caller is responsible for filtering to owned rows.
   * Single PostgREST round-trip via `.in('id', ids)`.
   */
  static async deleteExercises(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    try {
      const { error } = await supabase
        .from('exercise')
        .delete()
        .in('id', ids);
      if (error) {
        console.error('Error bulk-deleting exercises:', error);
        throw error;
      }
      clearExercisesCache();
    } catch (error) {
      console.error('Error in deleteExercises:', error);
      throw error;
    }
  }

  // Get a single exercise by ID
  static async getExerciseById(id: string): Promise<Exercise | null> {
    try {
      const { data, error } = await supabase
        .from('exercise')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching exercise:', error);
        throw error;
      }

      if (!data) return null;

      let exercise = rowToExercise(data);
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        const { data: override, error: overrideError } = await supabase
          .from('exercise_override')
          .select('*')
          .eq('trainer_id', user.id)
          .eq('exercise_id', id)
          .maybeSingle();
        if (overrideError) {
          console.warn('getExerciseById: exercise_override query failed', overrideError);
        }
        exercise = applyOverride(exercise, override ?? null);
      }
      return exercise;
    } catch (error) {
      console.error('Error in getExerciseById:', error);
      throw error;
    }
  }

  // Create a new exercise (trainers only)
  static async createExercise(exerciseData: CreateExerciseData): Promise<Exercise> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const description =
        exerciseData.description ??
        (exerciseData.steps?.length ? stepsToDescription(exerciseData.steps) : undefined);
      const basePayload = {
        name: exerciseData.name,
        muscle_group: exerciseData.muscleGroup,
        equipment: exerciseData.equipment ?? null,
        description,
        image: exerciseData.image,
        video: exerciseData.video,
        is_public: exerciseData.isPublic ?? false
      };

      // Try with created_by first so it shows under My Exercises
      const payloadWithCreator = user?.id ? { ...basePayload, created_by: user.id } : basePayload;
      const { data, error } = await supabase
        .from('exercise')
        .insert(payloadWithCreator)
        .select()
        .single();

      if (error) {
        const code = (error as { code?: string }).code;
        // Foreign key violation: created_by not in users(id). Retry without it so exercise is at least created (Library).
        if (code === '23503' && user?.id) {
          const { data: dataFallback, error: errorFallback } = await supabase
            .from('exercise')
            .insert(basePayload)
            .select()
            .single();
          if (errorFallback) {
            console.error('Error creating exercise (fallback):', errorFallback);
            throw new Error(errorFallback.message || 'Failed to create exercise.');
          }
          return {
            id: dataFallback.id,
            name: dataFallback.name,
            muscleGroup: dataFallback.muscle_group,
            equipment: dataFallback.equipment ?? undefined,
            description: dataFallback.description,
            steps: getExecutionStepsForDisplay(
              dataFallback.description,
              Array.isArray(dataFallback.steps) ? dataFallback.steps : [],
            ),
            image: dataFallback.image,
            video: dataFallback.video,
            isPublic: dataFallback.is_public,
            createdBy: dataFallback.created_by,
            createdAt: dataFallback.created_at,
            updatedAt: dataFallback.updated_at
          };
        }
        if (code === '23505') {
          throw new Error('You already have an exercise with this name. Please choose a different name.');
        }
        console.error('Error creating exercise:', error);
        throw new Error(error.message || 'Failed to create exercise.');
      }

      clearExercisesCache();
      // Transform and return the created exercise (NEW SCHEMA)
      return {
        id: data.id,
        name: data.name,
        muscleGroup: data.muscle_group,
        equipment: data.equipment ?? undefined,
        description: data.description,
        steps: getExecutionStepsForDisplay(
          data.description,
          Array.isArray(data.steps) ? data.steps : [],
        ),
        image: data.image,
        video: data.video,
        isPublic: data.is_public,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

    } catch (error) {
      console.error('Error in createExercise:', error);
      throw error;
    }
  }

  // Update an existing exercise (owner only)
  static async updateExercise(id: string, updates: Partial<CreateExerciseData>): Promise<Exercise> {
    try {
      const updateData: any = {};
      
      // Map our interface fields to database fields (NEW SCHEMA)
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.muscleGroup !== undefined) updateData.muscle_group = updates.muscleGroup;
      if (updates.equipment !== undefined) updateData.equipment = updates.equipment;
      if (updates.steps !== undefined) updateData.steps = updates.steps ?? null;
      if (updates.image !== undefined) updateData.image = updates.image;
      if (updates.video !== undefined) updateData.video = updates.video;
      if (updates.isPublic !== undefined) updateData.is_public = updates.isPublic;

      const { data, error } = await supabase
        .from('exercise')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating exercise:', error);
        throw error;
      }

      clearExercisesCache();
      // Transform and return the updated exercise (NEW SCHEMA)
      return {
        id: data.id,
        name: data.name,
        muscleGroup: data.muscle_group,
        equipment: data.equipment ?? undefined,
        description: data.description,
        steps: getExecutionStepsForDisplay(
          data.description,
          Array.isArray(data.steps) ? data.steps : [],
        ),
        image: data.image,
        video: data.video,
        isPublic: data.is_public,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

    } catch (error) {
      console.error('Error in updateExercise:', error);
      throw error;
    }
  }

  /**
   * Distinct muscle_group / equipment tokens from `exercise` rows (split on comma/pipe).
   * Used by trainer exercise edit chip pickers; capped in UI — query bounded by row limit.
   */
  static async getDistinctExerciseTagValues(): Promise<{ muscle: string[]; equipment: string[] }> {
    const muscle = new Set<string>();
    const equipment = new Set<string>();
    const { data, error } = await supabase
      .from('exercise')
      .select('muscle_group, equipment')
      .limit(4000);
    if (error) {
      console.error('getDistinctExerciseTagValues:', error);
      throw error;
    }
    const addTokens = (raw: string | null | undefined, set: Set<string>) => {
      if (!raw || !String(raw).trim()) return;
      for (const t of String(raw).split(/[,|]/).map((x) => x.trim()).filter(Boolean)) {
        set.add(t);
      }
    };
    for (const row of data ?? []) {
      addTokens((row as { muscle_group?: string }).muscle_group, muscle);
      addTokens((row as { equipment?: string }).equipment, equipment);
    }
    return {
      muscle: Array.from(muscle).sort((a, b) => a.localeCompare(b)),
      equipment: Array.from(equipment).sort((a, b) => a.localeCompare(b)),
    };
  }

  /** Save a trainer-specific override for a library exercise (created_by is null). Other trainers see the original. */
  static async upsertExerciseOverride(exerciseId: string, data: Partial<CreateExerciseData>): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error('Not authenticated');
      const payload: Record<string, any> = {
        trainer_id: user.id,
        exercise_id: exerciseId,
        updated_at: new Date().toISOString()
      };
      if (data.name !== undefined) payload.name = data.name;
      if (data.muscleGroup !== undefined) payload.muscle_group = data.muscleGroup;
      if (data.equipment !== undefined) payload.equipment = data.equipment;
      if (data.description !== undefined) payload.description = data.description;
      if (data.steps !== undefined) payload.steps = data.steps ?? [];
      if (data.image !== undefined) payload.image = data.image;
      if (data.video !== undefined) payload.video = data.video;
      if (data.isPublic !== undefined) payload.is_public = data.isPublic;
      const { error } = await supabase
        .from('exercise_override')
        .upsert(payload, { onConflict: 'trainer_id,exercise_id' });
      if (error) {
        console.error('Error upserting exercise override:', error);
        throw error;
      }
      clearExercisesCache();
    } catch (error) {
      console.error('Error in upsertExerciseOverride:', error);
      throw error;
    }
  }

  /** Remove a trainer's override so they see the library original again. */
  static async deleteExerciseOverride(exerciseId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return;
      await supabase
        .from('exercise_override')
        .delete()
        .eq('trainer_id', user.id)
        .eq('exercise_id', exerciseId);
      clearExercisesCache();
    } catch (error) {
      console.error('Error in deleteExerciseOverride:', error);
      throw error;
    }
  }

  // Delete an exercise (owner only)
  static async deleteExercise(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('exercise')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting exercise:', error);
        throw error;
      }
      clearExercisesCache();
    } catch (error) {
      console.error('Error in deleteExercise:', error);
      throw error;
    }
  }

  // Get exercise statistics
  static async getExerciseStats(): Promise<{
    total: number;
    byCategory: Record<string, number>;
    byDifficulty: Record<string, number>;
  }> {
    try {
      // NEW SCHEMA: category and difficulty removed from exercise table
      // This method may need to be removed or refactored
      const { data, error } = await supabase
        .from('exercise')
        .select('*');

      if (error) {
        console.error('Error fetching exercise stats:', error);
        throw error;
      }

      const stats = {
        total: data?.length || 0,
        byCategory: {} as Record<string, number>,
        byDifficulty: {} as Record<string, number>
      };

      // NEW SCHEMA: category and difficulty removed
      // Stats simplified - may need refactoring
      data?.forEach(() => {
        // Stats tracking removed for now
      });

      return stats;

    } catch (error) {
      console.error('Error in getExerciseStats:', error);
      throw error;
    }
  }
}
